import { useDeferredValue, useId, useMemo, useState } from 'react';
import { Button } from '../Button';
import { CopyButton } from '../CopyButton';
import { StatusText } from '../StatusPill';
import { ArrowDownIcon, ArrowUpIcon, CloseIcon, DownloadIcon, SearchIcon, SortIcon } from '../icons';
import { cellNumber, toCSV, toTSV } from '../../utils/tableExport';
import { downloadBlob } from './download';

const collator = new Intl.Collator('fr', { numeric: true, sensitivity: 'base' });
const nf = new Intl.NumberFormat('fr-FR');
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
// Nombre brut pour le CSV (virgule décimale, sans séparateur de milliers), comme l'export de la carte
const csvNumber = (n) => String(n).replace('.', ',');

// Recherche insensible à la casse et aux accents
const fold = (text) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * Visionneuse « Tableau » : tri par colonne (texte ou nombre), filtre, copie pour
 * Excel (TSV) et export CSV des lignes affichées.
 * `payload.values` (facultatif) : nombres bruts alignés sur les lignes hors en-tête
 * (onglet « Données » d'un visuel) ; ils servent au tri et au CSV à la place du texte
 * affiché (« 1 234,5 FCFA/km »).
 */
export function TableArtifact({ artifact }) {
  const rows = artifact.payload?.rows;
  const values = artifact.payload?.values ?? null;
  // Références stables tant que l'artefact ne change pas : filtre et tri ne sont
  // recalculés qu'à bon escient (pas à chaque rafale du streaming)
  const header = useMemo(() => rows?.[0] ?? [], [rows]);
  const body = useMemo(() => rows?.slice(1) ?? [], [rows]);
  const [sort, setSort] = useState({ col: null, dir: 'asc' });
  const [filter, setFilter] = useState('');
  const deferredFilter = useDeferredValue(filter);
  const filterId = useId();
  const countId = useId();

  // Nombre d'une cellule (ligne `index` hors en-tête) : valeur brute si fournie, sinon texte
  // lu (un code « 007 » reste du texte, comme dans le message)
  const numberAt = useMemo(() => (values
    ? (index, col) => (isNum(values[index]?.[col]) ? values[index][col] : null)
    : (index, col) => cellNumber(body[index]?.[col] ?? '')), [values, body]);

  // Colonne numérique : toutes ses cellules non vides sont des nombres
  const numericCols = useMemo(() => header.map((_, col) => {
    const filled = body.map((r, index) => ({ v: r[col], index })).filter(({ v }) => v && v.trim() && v.trim() !== '—' && v.trim() !== '-');
    return filled.length > 0 && filled.every(({ index }) => numberAt(index, col) !== null);
  }), [header, body, numberAt]);

  // Indices des lignes affichées (filtre puis tri)
  const visibleIdx = useMemo(() => {
    const needle = fold(deferredFilter.trim());
    let list = body.map((_, index) => index);
    if (needle) list = list.filter((index) => body[index].some((cell) => fold(cell).includes(needle)));
    if (sort.col !== null) {
      const col = sort.col;
      const factor = sort.dir === 'asc' ? 1 : -1;
      list = list
        .map((index) => ({ row: body[index], index }))
        .sort((a, b) => {
          const x = a.row[col] ?? '';
          const y = b.row[col] ?? '';
          let cmp;
          if (numericCols[col]) {
            const nx = numberAt(a.index, col);
            const ny = numberAt(b.index, col);
            // Cellules vides toujours en fin de liste
            if (nx === null && ny === null) cmp = 0;
            else if (nx === null) return 1;
            else if (ny === null) return -1;
            else cmp = nx - ny;
          } else {
            cmp = collator.compare(x, y);
          }
          return cmp * factor || a.index - b.index;
        })
        .map(({ index }) => index);
    }
    return list;
  }, [body, deferredFilter, sort, numericCols, numberAt]);
  const visible = useMemo(() => visibleIdx.map((index) => body[index]), [visibleIdx, body]);

  const toggleSort = (col) => {
    setSort((current) => {
      if (current.col !== col) return { col, dir: numericCols[col] ? 'desc' : 'asc' };
      if (current.dir === (numericCols[col] ? 'desc' : 'asc')) return { col, dir: numericCols[col] ? 'asc' : 'desc' };
      return { col: null, dir: 'asc' };
    });
  };

  const exportRows = () => [header, ...visible];
  // Copie pour Excel : nombres bruts, unité dans l'en-tête ; valeurs brutes fournies
  // (onglet « Données ») alignées sur les lignes affichées
  const tsvText = () => toTSV(exportRows(), values ? { values: visibleIdx.map((index) => values[index]) } : undefined);
  // CSV : nombres bruts quand ils sont fournis (sinon le texte affiché)
  const csvExportRows = () => (values
    ? [header, ...visibleIdx.map((index) => body[index].map((text, col) => {
        const n = numericCols[col] ? numberAt(index, col) : null;
        return n === null ? text : csvNumber(n);
      }))]
    : exportRows());
  const filtered = visible.length !== body.length;

  if (!header.length) {
    return <p className="artifact-empty">Ce tableau est vide.</p>;
  }

  return (
    <div className="artifact-view artifact-table">
      <div className="artifact-toolbar" role="toolbar" aria-label="Actions sur le tableau">
        <div className="artifact-filter">
          <label htmlFor={filterId} className="sr-only">Filtrer les lignes</label>
          <SearchIcon size={14} className="artifact-filter__icon" />
          <input
            id={filterId}
            type="search"
            value={filter}
            placeholder="Filtrer les lignes…"
            aria-describedby={countId}
            onChange={(event) => setFilter(event.target.value)}
            onKeyDown={(event) => {
              // Échap vide d'abord le filtre ; le panneau se ferme au second appui
              if (event.key === 'Escape' && filter) {
                event.preventDefault();
                event.stopPropagation();
                setFilter('');
              }
            }}
          />
          {filter && (
            <button type="button" className="artifact-filter__clear" aria-label="Effacer le filtre" onClick={() => setFilter('')}>
              <CloseIcon size={12} />
            </button>
          )}
        </div>
        <div className="artifact-toolbar__actions">
          <CopyButton
            getText={tsvText}
            label={filtered ? 'Copier les lignes affichées (Excel)' : 'Copier le tableau (Excel)'}
            showLabel
            size={14}
          />
          <Button
            variant="ghost"
            size="sm"
            icon={DownloadIcon}
            onClick={() => downloadBlob(`﻿${toCSV(csvExportRows(), ';')}`, 'tableau.csv', 'text/csv;charset=utf-8')}
          >
            <span>Exporter CSV</span>
          </Button>
        </div>
      </div>

      <p id={countId} className="artifact-table__count ds-num" aria-live="polite">
        {filtered
          ? `${nf.format(visible.length)} ligne${visible.length > 1 ? 's' : ''} sur ${nf.format(body.length)}`
          : `${nf.format(body.length)} ligne${body.length > 1 ? 's' : ''}`}
      </p>

      <div className="artifact-table__scroll" tabIndex={0} role="region" aria-label="Tableau, défilement">
        <table>
          <thead>
            <tr>
              {header.map((label, col) => {
                const active = sort.col === col;
                const ariaSort = active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none';
                const SortGlyph = !active ? SortIcon : sort.dir === 'asc' ? ArrowUpIcon : ArrowDownIcon;
                return (
                  <th key={col} scope="col" aria-sort={ariaSort} className={numericCols[col] ? 'is-numeric' : ''}>
                    <button type="button" className={`artifact-sort ${active ? 'is-active' : ''}`} onClick={() => toggleSort(col)}>
                      <span>{label || `Colonne ${col + 1}`}</span>
                      <SortGlyph size={12} />
                      <span className="sr-only">
                        {active ? `, trié ${sort.dir === 'asc' ? 'croissant' : 'décroissant'}` : ', trier'}
                      </span>
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {visible.map((row, index) => (
              <tr key={index}>
                {header.map((_, col) => (
                  <td key={col} className={numericCols[col] ? 'is-numeric ds-num' : ''}>
                    {numericCols[col] ? row[col] ?? '' : <StatusText text={row[col] ?? ''} />}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {!visible.length && (
          <p className="artifact-empty">Aucune ligne ne correspond à « {deferredFilter} ».</p>
        )}
      </div>
    </div>
  );
}
